import { Type } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';

export interface DocumentOptions {
  breakAfterSection?: boolean;
}

export interface FormlyFieldConfigWithDisplayViewOptions
  extends FormlyFieldConfig {
  key: string;
  type: string;
  displayViewOptions?: {
    groupBy?: string;
    typeOverride?: string;
    props?: Record<string, unknown>;
    omit?: boolean;
  };
}

export interface DisplayViewDynamicComponents {
  component: Type<unknown>;
  inputs: Record<string, unknown>;
}

export const BlockTypes = {
  EDITOR: 'editor',
  DYNAMIC: 'dynamic',
  PAGE_BREAK: 'page-break',
} as const;

interface BlockBase {
  id: string;
  type: (typeof BlockTypes)[keyof typeof BlockTypes];
  order: number;
  required: boolean;
  hidden: boolean;
}

export interface DynamicBlock extends BlockBase {
  id: string;
  type: typeof BlockTypes.DYNAMIC;
  component: Type<unknown>;
  inputs: Record<string, unknown>;
}

export interface TemplateBlock extends BlockBase {
  id: string;
  type: typeof BlockTypes.EDITOR;
  initialValue: string;
}

export interface PageBreakBlock extends BlockBase {
  id: string;
  type: typeof BlockTypes.PAGE_BREAK;
}

export type Blocks = DynamicBlock | TemplateBlock | PageBreakBlock;

export const blockGuards = {
  isEditor: (block: Blocks): block is TemplateBlock =>
    block.type === BlockTypes.EDITOR,
  isDynamic: (block: Blocks): block is DynamicBlock =>
    block.type === BlockTypes.DYNAMIC,
};

export type EditorModel = Record<string, string>;

export type PageData = Record<string, Record<string, string>>;

export interface DocumentData {
  templateId: string;
  blocks: Blocks[];
  editorModel: EditorModel;
  pagesModel: PageData;
}

export interface DocumentBuilderData
  extends Omit<DocumentData, 'templateId' | 'blocks'> {
  blocks: BlockDef[];
}

export type BlockDef = Pick<Blocks, 'id' | 'type'>;
